import ActiveChipsRow from '../ActiveChipsRow';
import CheckboxList from '../CheckboxList';
import FilterDropdown from '../FilterDropdown';
import RangeRow from '../RangeRow';
import type { TourismMeta, TourismOption } from '../../api/tourism';
import type { useTourismFilter } from '../../hooks/useTourismFilter';

type FilterApi = ReturnType<typeof useTourismFilter>;

/**
 * Merkmalsfilter der Seite „Daten".
 *
 * Eigene Leiste statt `InserateFilterBar`: dort hängen zwölf Props an
 * `useInserateFilter` und sieben Dropdowns an der Sachbearbeiter-Domäne
 * (Status, Zuordnung, Vermieter, Verfahren, Buchungen), die auf einem
 * Dashboard für Tourismusbehörden nichts zu suchen haben — und Ortsteil,
 * Anbieter und Gruppen fehlen dort. Geteilt werden die Bausteine
 * (`FilterDropdown`, `RangeRow`, `CheckboxList`, `ActiveChipsRow`), nicht die
 * Leiste.
 */
export default function TourismFilterBar({
  api,
  meta,
  matched,
  total,
}: {
  api: FilterApi;
  meta: TourismMeta | undefined;
  matched: number;
  total: number;
}) {
  const { filter, setFilterState, resetFilter, toggleIn, badgeCounts, filterActive, activeChips } =
    api;

  const options = meta?.options;
  const values = (list: TourismOption[] | undefined) => (list ?? []).map(o => o.value);
  const counts = (list: TourismOption[] | undefined) =>
    Object.fromEntries((list ?? []).map(o => [o.value, o.count]));

  return (
    <div className="bg-white rounded-lg shadow p-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <FilterDropdown label="Objekt" badgeCount={badgeCounts.objekt}>
          <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Objekttyp</p>
              <CheckboxList
                options={values(options?.listing_types)}
                selected={filter.listingTypes}
                counts={counts(options?.listing_types)}
                onToggle={v => toggleIn('listingTypes', v)}
              />
            </div>
            <RangeRow
              label="Größe"
              unit="m²"
              minVal={filter.sizeMin}
              maxVal={filter.sizeMax}
              onChange={(min, max) => setFilterState(f => ({ ...f, sizeMin: min, sizeMax: max }))}
            />
            <Coverage meta={meta} field="size_sqm" />
            <RangeRow
              label="Gäste"
              minVal={filter.guestsMin}
              maxVal={filter.guestsMax}
              onChange={(min, max) =>
                setFilterState(f => ({ ...f, guestsMin: min, guestsMax: max }))
              }
            />
            <Coverage meta={meta} field="guests_count" />
            <RangeRow
              label="Schlafzimmer"
              minVal={filter.bedroomsMin}
              maxVal={filter.bedroomsMax}
              onChange={(min, max) =>
                setFilterState(f => ({ ...f, bedroomsMin: min, bedroomsMax: max }))
              }
            />
            <Coverage meta={meta} field="bedrooms" />
          </div>
        </FilterDropdown>

        <FilterDropdown label="Preis & Bewertung" badgeCount={badgeCounts.preis}>
          <div className="space-y-4">
            <RangeRow
              label="Preis pro Nacht"
              unit="€"
              minVal={filter.priceMin}
              maxVal={filter.priceMax}
              onChange={(min, max) => setFilterState(f => ({ ...f, priceMin: min, priceMax: max }))}
            />
            <RangeRow
              label="Bewertung"
              minPlaceholder="1"
              maxPlaceholder="5"
              minVal={filter.ratingMin}
              maxVal={filter.ratingMax}
              onChange={(min, max) =>
                setFilterState(f => ({ ...f, ratingMin: min, ratingMax: max }))
              }
            />
            <Coverage meta={meta} field="rating" />
          </div>
        </FilterDropdown>

        <FilterDropdown label="Lage" badgeCount={badgeCounts.lage}>
          <div className="space-y-4 max-h-96 overflow-y-auto pr-1">
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Ortsteil</p>
              <CheckboxList
                options={values(options?.neighborhoods)}
                selected={filter.neighborhoods}
                counts={counts(options?.neighborhoods)}
                onToggle={v => toggleIn('neighborhoods', v)}
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1.5">PLZ</p>
              <CheckboxList
                options={values(options?.zipcodes)}
                selected={filter.zipcodes}
                counts={counts(options?.zipcodes)}
                onToggle={v => toggleIn('zipcodes', v)}
              />
            </div>
          </div>
        </FilterDropdown>

        <FilterDropdown label="Anbieter" badgeCount={badgeCounts.anbieter}>
          <div className="max-h-96 overflow-y-auto pr-1">
            <p className="text-xs text-gray-400 mb-2 px-1">
              Wer das Angebot tatsächlich vermarktet – zeigt den Professionalisierungsgrad.
            </p>
            <CheckboxList
              options={values(options?.offer_providers)}
              selected={filter.offerProviders}
              counts={counts(options?.offer_providers)}
              onToggle={v => toggleIn('offerProviders', v)}
            />
          </div>
        </FilterDropdown>

        <FilterDropdown label="Gruppen" badgeCount={badgeCounts.gruppen}>
          <div className="max-h-96 overflow-y-auto pr-1">
            {(options?.gruppen ?? []).length === 0 ? (
              <p className="text-xs text-gray-400 px-1 py-2 text-center">
                Noch keine Gruppen angelegt
              </p>
            ) : (
              <div className="space-y-0.5">
                {options?.gruppen.map(g => (
                  <label
                    key={g.id}
                    className="flex items-center gap-2.5 py-1.5 px-1 cursor-pointer hover:bg-gray-50 rounded-lg"
                  >
                    <input
                      type="checkbox"
                      checked={filter.gruppeIds.includes(g.id)}
                      onChange={() => toggleIn('gruppeIds', g.id)}
                      className="rounded border-gray-300 text-gray-900"
                    />
                    <span className="text-sm flex-1 truncate">{g.name}</span>
                    <span className="text-xs text-gray-400 tabular-nums">{g.listings}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </FilterDropdown>

        <div className="ml-auto flex items-center gap-3">
          <span className="text-sm text-gray-500 tabular-nums">
            <span className="font-semibold text-gray-900">{matched}</span> von {total} Inseraten
          </span>
          {filterActive && (
            <button
              onClick={resetFilter}
              className="text-sm text-red-500 hover:text-red-700 transition-colors"
            >
              Zurücksetzen
            </button>
          )}
        </div>
      </div>

      <ActiveChipsRow chips={activeChips} />
    </div>
  );
}

/** Hinweis, für welchen Anteil der Inserate ein Merkmal überhaupt bekannt ist.
 *  Ohne ihn wirkt ein Filter über ein zu 71 % gefülltes Feld so verbindlich wie
 *  einer über ein vollständiges. */
function Coverage({ meta, field }: { meta: TourismMeta | undefined; field: string }) {
  const share = meta?.coverage?.[field];
  if (share == null || share >= 0.995) return null;
  return (
    <p className="text-xs text-gray-400 -mt-2">
      Für {Math.round(share * 100)} % der Inserate bekannt – die übrigen fallen bei diesem Filter
      heraus.
    </p>
  );
}
